import React from "react";
import { Calendar, Plus, Edit2, Trash2, Loader2, Save, X, Download } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { OrganizerEventForm } from "./OrganizerEventForm";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OrganizerEventsTab = ({
  events, loading, showEventForm, setShowEventForm,
  editingEvent, setEditingEvent, eventForm, setEventForm,
  handleEventSubmit, handleEditEvent, handleDeleteEvent,
  handleImageUpload, eventImageRef,
  availableEvents, showClaimModal, setShowClaimModal, handleClaimEvent,
  editingStock, setEditingStock, stockValue, setStockValue, handleUpdateStock,
  showCancelConfirm, setShowCancelConfirm, handleCancelEvent,
  addTicketType, removeTicketType, updateTicketType
}) => {
  if (showEventForm) {
    return (
      <OrganizerEventForm
        editingEvent={editingEvent}
        setEditingEvent={setEditingEvent}
        eventForm={eventForm}
        setEventForm={setEventForm}
        handleEventSubmit={handleEventSubmit}
        setShowEventForm={setShowEventForm}
        handleImageUpload={handleImageUpload}
        eventImageRef={eventImageRef}
        addTicketType={addTicketType}
        removeTicketType={removeTicketType}
        updateTicketType={updateTicketType}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button onClick={() => setShowEventForm(true)} className="flex-1">
          <Plus className="w-4 h-4 mr-2" />
          Créer un événement
        </Button>
        {availableEvents.length > 0 && (
          <Button variant="outline" onClick={() => setShowClaimModal(true)}>
            Récupérer ({availableEvents.length})
          </Button>
        )}
      </div>

      {showClaimModal && availableEvents.length > 0 && (
        <div className="glass-card rounded-lg p-4 border-2 border-primary/30">
          <h4 className="font-semibold mb-3">Récupérer un événement</h4>
          <p className="text-sm text-muted-foreground mb-4">Ces événements peuvent être associés à votre compte.</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableEvents.map((event) => (
              <div key={event.id} className="flex items-center justify-between p-2 bg-muted/20 rounded-lg">
                <div>
                  <p className="font-medium text-sm">{event.title}</p>
                  <p className="text-xs text-muted-foreground">{event.artist} • {event.date}</p>
                </div>
                <Button size="sm" onClick={() => handleClaimEvent(event.id)}>Récupérer</Button>
              </div>
            ))}
          </div>
          <Button variant="outline" className="w-full mt-3" onClick={() => setShowClaimModal(false)}>Fermer</Button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">Chargement...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 glass-card rounded-lg">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Aucun événement</p>
          <p className="text-sm text-muted-foreground mt-1">
            {availableEvents.length > 0 ? "Récupérez un événement ou créez-en un !" : "Créez votre premier événement !"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <div key={event.id} className="glass-card rounded-lg p-4 flex gap-4">
              {event.image && <img src={event.image} alt="" className="w-20 h-20 rounded-lg object-cover" />}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold truncate">{event.title}</h4>
                <p className="text-sm text-muted-foreground">{event.artist}</p>
                <p className="text-xs text-muted-foreground mt-1">{event.date} • {event.venue}</p>
                <div className="flex flex-wrap gap-2 mt-2 items-center">
                  <span className="text-xs px-2 py-1 bg-primary/20 text-primary rounded">{event.price}€</span>
                  {editingStock === event.id ? (
                    <div className="flex items-center gap-1">
                      <Input type="number" value={stockValue} onChange={(e) => setStockValue(e.target.value)} className="w-20 h-7 text-xs" min="0" autoFocus />
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleUpdateStock(event.id)}><Save className="w-3 h-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingStock(null); setStockValue(""); }}><X className="w-3 h-3" /></Button>
                    </div>
                  ) : (
                    <span 
                      className={`text-xs px-2 py-1 rounded cursor-pointer flex items-center gap-1 ${(event.available_tickets || 0) === 0 ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'}`}
                      onClick={() => { setEditingStock(event.id); setStockValue((event.available_tickets || 0).toString()); }}
                    >
                      {event.available_tickets || 0} billets
                      <Edit2 className="w-3 h-3" />
                    </span>
                  )}
                  {(event.sold_out || (event.available_tickets || 0) === 0) && <span className="text-xs px-2 py-1 bg-red-500/20 text-red-500 rounded font-semibold">COMPLET</span>}
                  {event.status === 'cancelled' && <span className="text-xs px-2 py-1 bg-gray-500/20 text-gray-500 rounded font-semibold">ANNULÉ</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEditEvent(event)}><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDeleteEvent(event.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  data-testid={`export-csv-${event.id}`}
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem("tika_organizer_token");
                      const res = await fetch(`${API}/organizer/events/${event.id}/export`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      if (!res.ok) throw new Error("Erreur export");
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `export_${event.title.replace(/\s+/g, '_')}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                      toast.success("Export téléchargé");
                    } catch (err) {
                      toast.error("Erreur lors de l'export");
                    }
                  }}
                >
                  <Download className="w-3 h-3 mr-1" />
                  CSV
                </Button>
                {event.status !== 'cancelled' && (
                  <Button variant="outline" size="sm" className="text-xs text-red-500 border-red-500/30 hover:bg-red-500/10" onClick={() => setShowCancelConfirm(event.id)}>Annuler</Button>
                )}
              </div>
            </div>
          ))}
          
          {showCancelConfirm && (
            <div className="glass-card rounded-lg p-4 border-2 border-red-500/30 mt-4">
              <h4 className="font-bold text-red-500 mb-2">Annuler cet événement ?</h4>
              <p className="text-sm text-muted-foreground mb-4">Tous les acheteurs seront remboursés. Action irréversible.</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowCancelConfirm(null)} className="flex-1">Non, garder</Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={() => handleCancelEvent(showCancelConfirm)}>Oui, annuler</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
